import type { ReportSlide } from './reports/enhancedNarrativeSlides'
import { REPORT_FONT, renderReportSlideSvg } from './reports/enhancedNarrativeSlides'
import { importWithChunkRecovery } from './chunkLoadRecovery'

const fileName = (propertyName: string) => `${propertyName.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '') || 'property'}-enhanced-report`
const hex = (color: string) => color.replace('#', '')

export const exportEnhancedNarrativePptx = async (slides: ReportSlide[], propertyName: string) => {
    const { default: PptxGenJS } = await importWithChunkRecovery(() => import('pptxgenjs'))
    const pptx = new PptxGenJS()
    pptx.defineLayout({ name: 'ENHANCED_REPORT', width: 1280 / 96, height: 720 / 96 })
    pptx.layout = 'ENHANCED_REPORT'
    pptx.author = 'HotelWorld AI'
    pptx.subject = `AI-search readiness for ${propertyName}`
    pptx.title = `${propertyName} — Enhanced report`
    pptx.theme = { headFontFace: REPORT_FONT, bodyFontFace: REPORT_FONT }
    for (const page of slides) {
        const slide = pptx.addSlide()
        slide.background = { color: 'FFFFFF' }
        slide.addNotes(page.notes)
        for (const element of page.elements) {
            if (element.type === 'text') {
                slide.addText(element.text, {
                    x: element.x / 96, y: element.y / 96, w: (element.width + 4) / 96, h: element.size * 1.23 / 96,
                    fontFace: REPORT_FONT, fontSize: element.size * 0.75, bold: element.bold,
                    color: hex(element.color), margin: 0, breakLine: false, valign: 'top',
                    paraSpaceAfter: 0, ...(element.link ? { hyperlink: { url: element.link } } : {}),
                })
            } else if (element.type === 'rect') {
                slide.addShape(element.radius ? pptx.ShapeType.roundRect : pptx.ShapeType.rect, {
                    x: element.x / 96, y: element.y / 96, w: element.width / 96, h: element.height / 96,
                    rectRadius: (element.radius || 0) / 96,
                    fill: { color: hex(element.fill) },
                    line: { color: hex(element.stroke || element.fill), width: element.stroke ? 0.75 : 0 },
                })
            } else if (element.type === 'line') {
                // Editable chart segments use exactly the same coordinates as the preview.
                slide.addShape(pptx.ShapeType.line, {
                    x: Math.min(element.x, element.x2) / 96, y: Math.min(element.y, element.y2) / 96,
                    w: Math.abs(element.x2 - element.x) / 96, h: Math.abs(element.y2 - element.y) / 96,
                    flipV: (element.x2 - element.x) * (element.y2 - element.y) < 0,
                    line: { color: hex(element.color), width: element.width * 0.75, beginArrowType: 'none', endArrowType: 'none' },
                })
            } else {
                slide.addShape(pptx.ShapeType.ellipse, {
                    x: (element.x - element.radius) / 96, y: (element.y - element.radius) / 96,
                    w: element.radius * 2 / 96, h: element.radius * 2 / 96,
                    fill: { color: hex(element.fill) }, line: { color: hex(element.fill), width: 0 },
                })
            }
        }
    }
    await pptx.writeFile({ fileName: `${fileName(propertyName)}.pptx`, compression: true })
}

const slideImage = async (slide: ReportSlide): Promise<string> => {
    const url = URL.createObjectURL(new Blob([renderReportSlideSvg(slide)], { type: 'image/svg+xml;charset=utf-8' }))
    try {
        const image = new Image()
        await new Promise<void>((resolve, reject) => {
            image.onload = () => resolve()
            image.onerror = () => reject(new Error('Could not render a report page for the PDF. Please try again.'))
            image.src = url
        })
        const canvas = document.createElement('canvas')
        canvas.width = 2560
        canvas.height = 1440
        const context = canvas.getContext('2d')
        if (!context) throw new Error('Your browser could not prepare the PDF. Please try the PowerPoint download.')
        context.drawImage(image, 0, 0, canvas.width, canvas.height)
        return canvas.toDataURL('image/png')
    } finally {
        URL.revokeObjectURL(url)
    }
}

export const exportEnhancedNarrativePdf = async (slides: ReportSlide[], propertyName: string) => {
    const { jsPDF: JsPdf } = await importWithChunkRecovery(() => import('jspdf'))
    const pdf = new JsPdf({ orientation: 'landscape', unit: 'pt', format: [960, 540], compress: true })
    pdf.setProperties({ title: `${propertyName} — Enhanced report`, author: 'HotelWorld AI' })
    for (let index = 0; index < slides.length; index += 1) {
        if (index) pdf.addPage([960, 540], 'landscape')
        pdf.addImage(await slideImage(slides[index]), 'PNG', 0, 0, 960, 540, undefined, 'FAST')
        // Retain clickable citations on the rendered PDF pages.
        for (const element of slides[index].elements) {
            if (element.type === 'text' && element.link) {
                pdf.link(element.x * 0.75, element.y * 0.75, element.width * 0.75, element.size, { url: element.link })
            }
        }
    }
    pdf.save(`${fileName(propertyName)}.pdf`)
}
